import { useState } from 'react'
import { SlidersHorizontal } from 'lucide-react'
import { Button, Popover, Select } from '@/components/common'
import { collectionChoices } from './collectionModel'
import { ReferencePicker, AudiencesInput } from './ReferencePicker'
import styles from './nativeWorkspace.module.css'

const key = (persona) => JSON.stringify({
  teams: persona?.teams || [],
  competitions: persona?.competitions || [],
  history_teams: persona?.history_teams || [],
  audiences: persona?.audiences || [],
})


export function PreviewAudienceControl({ workspace }) {
  const [open, setOpen] = useState(false)
  const selected = workspace.sessionState === 'anonymous' ? 'anonymous' : key(workspace.previewPersona)
  const choices = [...workspace.personas]
  if (workspace.previewPersona && !choices.some((persona) => key(persona) === selected)) choices.push(workspace.previewPersona)

  // Source choices cover identities beyond the displayed inventory limit, so
  // the persona can name any team the runtime knows, not only the first page.
  const displayedChoices = collectionChoices(workspace.previewCatalog?.events || [])
  const sourceChoices = workspace.previewCatalog?.choices || {}
  const merge = (items, keyOf) => [...new Map(items.map((item) => [keyOf(item), item])).values()]
  const teamChoices = merge([...displayedChoices.teams, ...(sourceChoices.teams || [])], (team) => `${team.sport}:${team.id}`).sort((a, b) => a.name.localeCompare(b.name))
  const compChoices = merge([...displayedChoices.competition_scopes, ...(sourceChoices.competition_scopes || [])], (comp) => `${comp.sport}:${comp.id}`)

  const updatePersonaField = (field, value) => {
    if (!workspace.previewPersona) return
    const currentName = workspace.previewPersona.name || 'Custom'
    const name = currentName.includes('(custom)') ? currentName : `${currentName} (custom)`.slice(0, 100)
    const updated = {
      ...workspace.previewPersona,
      [field]: value,
      name,
    }
    workspace.setPreviewPersona(updated)
  }

  return (
    <div className={styles.toolbarPair}>
      <label className={styles.toolbarField}>
        <span>View as</span>
        <Select size="compact" aria-label="View as" value={selected} onChange={(event) => {
          if (event.target.value === 'anonymous') { workspace.setSessionState('anonymous'); workspace.setPreviewPersona(null); return }
          const persona = choices.find((item) => key(item) === event.target.value)
          if (persona) { workspace.setSessionState('authenticated'); workspace.setPreviewPersona({ ...persona, id: crypto.randomUUID() }) }
        }}>
          <option value="anonymous">Anonymous visitor</option>
          {choices.map((persona) => <option key={key(persona)} value={key(persona)}>{persona.name}</option>)}
        </Select>
      </label>
      {workspace.sessionState === 'authenticated' && workspace.previewPersona ? (
        <Popover
          open={open}
          onOpenChange={setOpen}
          label="Customize persona"
          trigger={
            <Button size="compact" variant="ghost" aria-label="Customize persona">
              <SlidersHorizontal size={13} />
              Customize
            </Button>
          }
        >
          <div style={{ padding: '12px', display: 'grid', gap: '12px', width: '280px', maxWidth: '100vw' }}>
            <strong>Customize preview persona</strong>
            <ReferencePicker
              label="Followed teams"
              values={workspace.previewPersona.teams || []}
              choices={teamChoices}
              onChange={(teams) => updatePersonaField('teams', teams)}
            />
            <ReferencePicker
              label="Followed competitions"
              values={workspace.previewPersona.competitions || []}
              choices={compChoices}
              onChange={(competitions) => updatePersonaField('competitions', competitions)}
            />
            <ReferencePicker
              label="Wagering history teams"
              values={workspace.previewPersona.history_teams || []}
              choices={teamChoices}
              onChange={(history_teams) => updatePersonaField('history_teams', history_teams)}
            />
            <AudiencesInput
              audiences={workspace.previewPersona.audiences || []}
              onChange={(audiences) => updatePersonaField('audiences', audiences)}
            />
          </div>
        </Popover>
      ) : null}
    </div>
  )
}
